import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { FSMainFeature, FSProject, FSReportData } from "../../Types/FinishingSchedule/FinishingSchedule";
import { FSAppliance } from "../../Types/FinishingSchedule/Appliance";
import { FSBrand } from "../../Types/FinishingSchedule/Brand";
import { FSColor } from "../../Types/FinishingSchedule/Color";
import { FSDocument } from "../../Types/FinishingSchedule/Document";
import { FSItem } from "../../Types/FinishingSchedule/Item";
import { FSLocation } from "../../Types/FinishingSchedule/Location";
import { FSSize } from "../../Types/FinishingSchedule/Size";
import { FSType } from "../../Types/FinishingSchedule/Type";
import { FSPaint } from "../../Types/FinishingSchedule/Paint/Paint";
import { FSAccessory } from "../../Types/FinishingSchedule/Accessory/Accessory";
import { FSAccessorySub } from "../../Types/FinishingSchedule/Accessory/AccessorySub";
import { FSFloorFinishing } from "../../Types/FinishingSchedule/FloorFinishing/FloorFinishing";
import { FSTile } from "../../Types/FinishingSchedule/Tile/Tile";
import { FSTileShop } from "../../Types/FinishingSchedule/Tile/TileShop";
import { FSDoor } from "../../Types/FinishingSchedule/Door/Door";
import { FSDoorSize } from "../../Types/FinishingSchedule/Door/DoorSize";
import { FSBathroom } from "../../Types/FinishingSchedule/Bathroom/Bathroom";
import { FSBathroomFeature } from "../../Types/FinishingSchedule/Bathroom/BathroomFeature";
import { FSKitchen } from "../../Types/FinishingSchedule/Kitchen/Kitchen";
import { FSKitchenCollection } from "../../Types/FinishingSchedule/Kitchen/KitchenCollection";
import { FSKitchenFinish } from "../../Types/FinishingSchedule/Kitchen/KitchenFinish";
import { FSKitchenRange } from "../../Types/FinishingSchedule/Kitchen/KitchenRange";

import {
    fetchFSProjectList,
    fetchFSApplianceList,
    fetchFSBrandList,
    fetchFSColorList,
    fetchFSDocumentList,
    fetchFSItemList,
    fetchFSLocationList,
    fetchFSSizeList,
    fetchFSTypeList,
    fetchFSPaintList,
    fetchFSAccessoryList,
    fetchFSAccessorySubList,
    fetchFSFloorFinishingList,
    fetchFSTileList,
    fetchFSTileShopList,
    fetchFSDoorList,
    fetchFSDoorSizeList,
    fetchFSBathroomList,
    fetchFSBathroomFeatureList,
    fetchFSKitchenList,
    fetchFSMainFeatureList,
    fetchFSKitchenCollectionList,
    fetchFSKitchenFinishList,
    fetchFSKitchenRangeList,
    fetchFSLightList,
    fetchFSOtherList,
    fetchFSReportData,
    fetchFSKitchenStyleList
} from "../../Thunks/FinishingScheduleThunks";
import { FSLight } from "../../Types/FinishingSchedule/Light/Light";
import { FSOther } from "../../Types/FinishingSchedule/Other/Other";
import { FSKitchenStyle } from "../../Types/FinishingSchedule/Kitchen/KitchenStyle";

interface FinishingScheduleSliceState {
    loadingFSProjects: boolean;
    fsProjects: FSProject[];
    errorFSProjects: string | null;

    loadingFSMainFeatures: boolean;
    fsMainFeatures: FSMainFeature[];
    errorFSMainFeatures: string | null;

    loadingFSAppliances: boolean;
    fsAppliances: FSAppliance[];
    errorFSAppliances: string | null;

    loadingFSBrands: boolean;
    fsBrands: FSBrand[];
    errorFSBrands: string | null;

    loadingFSColors: boolean;
    fsColors: FSColor[];
    errorFSColors: string | null;

    loadingFSDocuments: boolean;
    fsDocuments: FSDocument[];
    errorFSDocuments: string | null;

    loadingFSItems: boolean;
    fsItems: FSItem[];
    errorFSItems: string | null;

    loadingFSLocations: boolean;
    fsLocations: FSLocation[];
    errorFSLocations: string | null;

    loadingFSSizes: boolean;
    fsSizes: FSSize[];
    errorFSSizes: string | null;

    loadingFSTypes: boolean;
    fsTypes: FSType[];
    errorFSTypes: string | null;

    loadingFSPaints: boolean;
    fsPaints: FSPaint[];
    errorFSPaints: string | null;

    loadingFSAccessories: boolean;
    fsAccessories: FSAccessory[];
    errorFSAccessories: string | null;

    loadingFSSubAccessories: boolean;
    fsSubAccessories: FSAccessorySub[];
    errorFSSubAccessories: string | null;

    loadingFSFloorFinishing: boolean;
    fsFloorFinishing: FSFloorFinishing | null;
    errorFSFloorFinishing: string | null;

    loadingFSTiles: boolean;
    fsTiles: FSTile[];
    errorFSTiles: string | null;

    loadingFSTileShops: boolean;
    fsTileShops: FSTileShop[];
    errorFSTileShops: string | null;

    loadingFSDoors: boolean;
    fsDoors: FSDoor[];
    errorFSDoors: string | null;

    loadingFSDoorSizes: boolean;
    fsDoorSizes: FSDoorSize[];
    errorFSDoorSizes: string | null;

    loadingFSBathrooms: boolean;
    fsBathrooms: FSBathroom[];
    errorFSBathrooms: string | null;

    loadingFSBathroomFeatures: boolean;
    fsBathroomFeatures: FSBathroomFeature[];
    errorFSBathroomFeatures: string | null;

    loadingFSKitchens: boolean;
    fsKitchens: FSKitchen[];
    errorFSKitchens: string | null;

    loadingFSKitchenCollections: boolean;
    fsKitchenCollections: FSKitchenCollection[];
    errorFSKitchenCollections: string | null;

    loadingFSKitchenFinishes: boolean;
    fsKitchenFinishes: FSKitchenFinish[];
    errorFSKitchenFinishes: string | null;

    loadingFSKitchenRanges: boolean;
    fsKitchenRanges: FSKitchenRange[];
    errorFSKitchenRanges: string | null;

    loadingFSKitchenStyles: boolean;
    fsKitchenStyles: FSKitchenStyle[];
    errorFSKitchenStyles: string | null;

    loadingFSLights: boolean;
    fsLights: FSLight[];
    errorFSLights: string | null;

    loadingFSOthers: boolean;
    fsOthers: FSOther[];
    errorFSOthers: string | null;

    loadingFSReportData: boolean;
    fsReportData: FSReportData | null;
    errorFSReportData: string | null;
}

// set inital valus
const initialState: FinishingScheduleSliceState = {
    loadingFSProjects: false,
    fsProjects: [],
    errorFSProjects: '',

    loadingFSMainFeatures: false,
    fsMainFeatures: [],
    errorFSMainFeatures: '',

    loadingFSAppliances: false,
    fsAppliances: [],
    errorFSAppliances: '',

    loadingFSBrands: false,
    fsBrands: [],
    errorFSBrands: '',

    loadingFSColors: false,
    fsColors: [],
    errorFSColors: '',

    loadingFSDocuments: false,
    fsDocuments: [],
    errorFSDocuments: '',

    loadingFSItems: false,
    fsItems: [],
    errorFSItems: '',

    loadingFSLocations: false,
    fsLocations: [],
    errorFSLocations: '',

    loadingFSSizes: false,
    fsSizes: [],
    errorFSSizes: '',

    loadingFSTypes: false,
    fsTypes: [],
    errorFSTypes: '',

    loadingFSPaints: false,
    fsPaints: [],
    errorFSPaints: '',

    loadingFSAccessories: false,
    fsAccessories: [],
    errorFSAccessories: '',

    loadingFSSubAccessories: false,
    fsSubAccessories: [],
    errorFSSubAccessories: '',

    loadingFSFloorFinishing: false,
    fsFloorFinishing: null,
    errorFSFloorFinishing: '',

    loadingFSTiles: false,
    fsTiles: [],
    errorFSTiles: '',

    loadingFSTileShops: false,
    fsTileShops: [],
    errorFSTileShops: '',

    loadingFSDoors: false,
    fsDoors: [],
    errorFSDoors: '',

    loadingFSDoorSizes: false,
    fsDoorSizes: [],
    errorFSDoorSizes: '',

    loadingFSBathrooms: false,
    fsBathrooms: [],
    errorFSBathrooms: '',

    loadingFSBathroomFeatures: false,
    fsBathroomFeatures: [],
    errorFSBathroomFeatures: '',

    loadingFSKitchens: false,
    fsKitchens: [],
    errorFSKitchens: '',

    loadingFSKitchenCollections: false,
    fsKitchenCollections: [],
    errorFSKitchenCollections: '',

    loadingFSKitchenFinishes: false,
    fsKitchenFinishes: [],
    errorFSKitchenFinishes: '',

    loadingFSKitchenRanges: false,
    fsKitchenRanges: [],
    errorFSKitchenRanges: '',

    loadingFSKitchenStyles: false,
    fsKitchenStyles: [],
    errorFSKitchenStyles: '',

    loadingFSLights: false,
    fsLights: [],
    errorFSLights: '',

    loadingFSOthers: false,
    fsOthers: [],
    errorFSOthers: '',

    loadingFSReportData: false,
    fsReportData: null,
    errorFSReportData: '',
}

// set state value for api response
const finishingScheduleSlice = createSlice({
    name: 'finishingSchedule',
    initialState,
    reducers: {
    },
    extraReducers(builder) {
        builder
            /* Finishing shedule projects */
            .addCase(fetchFSProjectList.pending, state => {
                state.loadingFSProjects = true;
                state.errorFSProjects = null;
            })
            .addCase(fetchFSProjectList.fulfilled, (state, action: PayloadAction<FSProject[]>) => {
                state.loadingFSProjects = false;
                state.fsProjects = action.payload;
            })
            .addCase(fetchFSProjectList.rejected, (state, action) => {
                state.loadingFSProjects = false;
                state.errorFSProjects = action.error.message ?? "Failed to fetch projects.";
            })

            /* Finishing shedule main features */
            .addCase(fetchFSMainFeatureList.pending, state => {
                state.loadingFSMainFeatures = true;
                state.errorFSMainFeatures = null;
            })
            .addCase(fetchFSMainFeatureList.fulfilled, (state, action: PayloadAction<FSMainFeature[]>) => {
                state.loadingFSMainFeatures = false;

                const activeData = (action.payload ?? []).filter(e => e.status === 1);
                state.fsMainFeatures = activeData;
            })
            .addCase(fetchFSMainFeatureList.rejected, (state, action) => {
                state.loadingFSMainFeatures = false;
                state.errorFSMainFeatures = action.error.message ?? "Failed to fetch features.";
            })

            /* Finishing shedule appliances */
            .addCase(fetchFSApplianceList.pending, state => {
                state.loadingFSAppliances = true;
                state.errorFSAppliances = null;
            })
            .addCase(fetchFSApplianceList.fulfilled, (state, action: PayloadAction<FSAppliance[]>) => {
                state.loadingFSAppliances = false;
                state.fsAppliances = action.payload;
            })
            .addCase(fetchFSApplianceList.rejected, (state, action) => {
                state.loadingFSAppliances = false;
                state.errorFSAppliances = action.error.message ?? "Failed to fetch appliances.";
            })

            /* Finishing shedule brands */
            .addCase(fetchFSBrandList.pending, state => {
                state.loadingFSBrands = true;
                state.errorFSBrands = null;
            })
            .addCase(fetchFSBrandList.fulfilled, (state, action: PayloadAction<FSBrand[]>) => {
                state.loadingFSBrands = false;
                state.fsBrands = action.payload;
            })
            .addCase(fetchFSBrandList.rejected, (state, action) => {
                state.loadingFSBrands = false;
                state.errorFSBrands = action.error.message ?? "Failed to fetch brands.";
            })

            /* Finishing shedule colors */
            .addCase(fetchFSColorList.pending, state => {
                state.loadingFSColors = true;
                state.errorFSColors = null;
            })
            .addCase(fetchFSColorList.fulfilled, (state, action: PayloadAction<FSColor[]>) => {
                state.loadingFSColors = false;
                state.fsColors = action.payload;
            })
            .addCase(fetchFSColorList.rejected, (state, action) => {
                state.loadingFSColors = false;
                state.errorFSColors = action.error.message ?? "Failed to fetch colors.";
            })

            /* Finishing shedule documents */
            .addCase(fetchFSDocumentList.pending, state => {
                state.loadingFSDocuments = true;
                state.errorFSDocuments = null;
            })
            .addCase(fetchFSDocumentList.fulfilled, (state, action: PayloadAction<FSDocument[]>) => {
                state.loadingFSDocuments = false;
                state.fsDocuments = action.payload;
            })
            .addCase(fetchFSDocumentList.rejected, (state, action) => {
                state.loadingFSDocuments = false;
                state.errorFSDocuments = action.error.message ?? "Failed to fetch documents.";
            })

            /* Finishing shedule items */
            .addCase(fetchFSItemList.pending, state => {
                state.loadingFSItems = true;
                state.errorFSItems = null;
            })
            .addCase(fetchFSItemList.fulfilled, (state, action: PayloadAction<FSItem[]>) => {
                state.loadingFSItems = false;
                state.fsItems = action.payload;
            })
            .addCase(fetchFSItemList.rejected, (state, action) => {
                state.loadingFSItems = false;
                state.errorFSItems = action.error.message ?? "Failed to fetch items.";
            })

            /* Finishing shedule locations */
            .addCase(fetchFSLocationList.pending, state => {
                state.loadingFSLocations = true;
                state.errorFSLocations = null;
            })
            .addCase(fetchFSLocationList.fulfilled, (state, action: PayloadAction<FSLocation[]>) => {
                state.loadingFSLocations = false;
                state.fsLocations = action.payload;
            })
            .addCase(fetchFSLocationList.rejected, (state, action) => {
                state.loadingFSLocations = false;
                state.errorFSLocations = action.error.message ?? "Failed to fetch locations.";
            })

            /* Finishing shedule sizes */
            .addCase(fetchFSSizeList.pending, state => {
                state.loadingFSSizes = true;
                state.errorFSSizes = null;
            })
            .addCase(fetchFSSizeList.fulfilled, (state, action: PayloadAction<FSSize[]>) => {
                state.loadingFSSizes = false;
                state.fsSizes = action.payload;
            })
            .addCase(fetchFSSizeList.rejected, (state, action) => {
                state.loadingFSSizes = false;
                state.errorFSSizes = action.error.message ?? "Failed to fetch sizes.";
            })

            /* Finishing shedule types */
            .addCase(fetchFSTypeList.pending, state => {
                state.loadingFSTypes = true;
                state.errorFSTypes = null;
            })
            .addCase(fetchFSTypeList.fulfilled, (state, action: PayloadAction<FSType[]>) => {
                state.loadingFSTypes = false;
                state.fsTypes = action.payload;
            })
            .addCase(fetchFSTypeList.rejected, (state, action) => {
                state.loadingFSTypes = false;
                state.errorFSTypes = action.error.message ?? "Failed to fetch types.";
            })

            /* Finishing shedule paints */
            .addCase(fetchFSPaintList.pending, state => {
                state.loadingFSPaints = true;
                state.errorFSPaints = null;
            })
            .addCase(fetchFSPaintList.fulfilled, (state, action: PayloadAction<FSPaint[]>) => {
                state.loadingFSPaints = false;
                state.fsPaints = action.payload;
            })
            .addCase(fetchFSPaintList.rejected, (state, action) => {
                state.loadingFSPaints = false;
                state.errorFSPaints = action.error.message ?? "Failed to fetch paints.";
            })

            /* Finishing shedule accessory names */
            .addCase(fetchFSAccessoryList.pending, state => {
                state.loadingFSAccessories = true;
                state.errorFSAccessories = null;
            })
            .addCase(fetchFSAccessoryList.fulfilled, (state, action: PayloadAction<FSAccessory[]>) => {
                state.loadingFSAccessories = false;
                state.fsAccessories = action.payload;
            })
            .addCase(fetchFSAccessoryList.rejected, (state, action) => {
                state.loadingFSAccessories = false;
                state.errorFSAccessories = action.error.message ?? "Failed to fetch accessories.";
            })

            /* Finishing shedule accessory sub names */
            .addCase(fetchFSAccessorySubList.pending, state => {
                state.loadingFSSubAccessories = true;
                state.errorFSSubAccessories = null;
            })
            .addCase(fetchFSAccessorySubList.fulfilled, (state, action: PayloadAction<FSAccessorySub[]>) => {
                state.loadingFSSubAccessories = false;
                state.fsSubAccessories = action.payload;
            })
            .addCase(fetchFSAccessorySubList.rejected, (state, action) => {
                state.loadingFSSubAccessories = false;
                state.errorFSSubAccessories = action.error.message ?? "Failed to fetch sub accessories.";
            })

            /* Finishing shedule floor finishings */
            .addCase(fetchFSFloorFinishingList.pending, state => {
                state.loadingFSFloorFinishing = true;
                state.errorFSFloorFinishing = null;
            })
            .addCase(fetchFSFloorFinishingList.fulfilled, (state, action: PayloadAction<FSFloorFinishing | null>) => {
                state.loadingFSFloorFinishing = false;
                state.fsFloorFinishing = action.payload;
            })
            .addCase(fetchFSFloorFinishingList.rejected, (state, action) => {
                state.loadingFSFloorFinishing = false;
                state.errorFSFloorFinishing = action.error.message ?? "Failed to fetch floor finishings.";
            })

            /* Finishing shedule tiles */
            .addCase(fetchFSTileList.pending, state => {
                state.loadingFSTiles = true;
                state.errorFSTiles = null;
            })
            .addCase(fetchFSTileList.fulfilled, (state, action: PayloadAction<FSTile[]>) => {
                state.loadingFSTiles = false;
                state.fsTiles = action.payload;
            })
            .addCase(fetchFSTileList.rejected, (state, action) => {
                state.loadingFSTiles = false;
                state.errorFSTiles = action.error.message ?? "Failed to fetch tiles.";
            })

            /* Finishing shedule shops */
            .addCase(fetchFSTileShopList.pending, state => {
                state.loadingFSTileShops = true;
                state.errorFSTileShops = null;
            })
            .addCase(fetchFSTileShopList.fulfilled, (state, action: PayloadAction<FSTileShop[]>) => {
                state.loadingFSTileShops = false;
                state.fsTileShops = action.payload;
            })
            .addCase(fetchFSTileShopList.rejected, (state, action) => {
                state.loadingFSTileShops = false;
                state.errorFSTileShops = action.error.message ?? "Failed to fetch shops.";
            })

            /* Finishing shedule doors */
            .addCase(fetchFSDoorList.pending, state => {
                state.loadingFSDoors = true;
                state.errorFSDoors = null;
            })
            .addCase(fetchFSDoorList.fulfilled, (state, action: PayloadAction<FSDoor[]>) => {
                state.loadingFSDoors = false;
                state.fsDoors = action.payload;
            })
            .addCase(fetchFSDoorList.rejected, (state, action) => {
                state.loadingFSDoors = false;
                state.errorFSDoors = action.error.message ?? "Failed to fetch doors.";
            })

            /* Finishing shedule door sizes */
            .addCase(fetchFSDoorSizeList.pending, state => {
                state.loadingFSDoorSizes = true;
                state.errorFSDoorSizes = null;
            })
            .addCase(fetchFSDoorSizeList.fulfilled, (state, action: PayloadAction<FSDoorSize[]>) => {
                state.loadingFSDoorSizes = false;
                state.fsDoorSizes = action.payload;
            })
            .addCase(fetchFSDoorSizeList.rejected, (state, action) => {
                state.loadingFSDoorSizes = false;
                state.errorFSDoorSizes = action.error.message ?? "Failed to fetch door sizes.";
            })

            /* Finishing shedule bathrooms */
            .addCase(fetchFSBathroomList.pending, state => {
                state.loadingFSBathrooms = true;
                state.errorFSBathrooms = null;
            })
            .addCase(fetchFSBathroomList.fulfilled, (state, action: PayloadAction<FSBathroom[]>) => {
                state.loadingFSBathrooms = false;
                state.fsBathrooms = action.payload;
            })
            .addCase(fetchFSBathroomList.rejected, (state, action) => {
                state.loadingFSBathrooms = false;
                state.errorFSBathrooms = action.error.message ?? "Failed to fetch bathrooms.";
            })

            /* Finishing shedule bathroom features */
            .addCase(fetchFSBathroomFeatureList.pending, state => {
                state.loadingFSBathroomFeatures = true;
                state.errorFSBathroomFeatures = null;
            })
            .addCase(fetchFSBathroomFeatureList.fulfilled, (state, action: PayloadAction<FSBathroomFeature[]>) => {
                state.loadingFSBathroomFeatures = false;
                state.fsBathroomFeatures = action.payload;
            })
            .addCase(fetchFSBathroomFeatureList.rejected, (state, action) => {
                state.loadingFSBathroomFeatures = false;
                state.errorFSBathroomFeatures = action.error.message ?? "Failed to fetch bathroom features.";
            })

            /* Finishing shedule kitchens */
            .addCase(fetchFSKitchenList.pending, state => {
                state.loadingFSKitchens = true;
                state.errorFSKitchens = null;
            })
            .addCase(fetchFSKitchenList.fulfilled, (state, action: PayloadAction<FSKitchen[]>) => {
                state.loadingFSKitchens = false;
                state.fsKitchens = action.payload;
            })
            .addCase(fetchFSKitchenList.rejected, (state, action) => {
                state.loadingFSKitchens = false;
                state.errorFSKitchens = action.error.message ?? "Failed to fetch kitchens.";
            })

            /* Finishing shedule kitchen collections */
            .addCase(fetchFSKitchenCollectionList.pending, state => {
                state.loadingFSKitchenCollections = true;
                state.errorFSKitchenCollections = null;
            })
            .addCase(fetchFSKitchenCollectionList.fulfilled, (state, action: PayloadAction<FSKitchenCollection[]>) => {
                state.loadingFSKitchenCollections = false;
                state.fsKitchenCollections = action.payload;
            })
            .addCase(fetchFSKitchenCollectionList.rejected, (state, action) => {
                state.loadingFSKitchenCollections = false;
                state.errorFSKitchenCollections = action.error.message ?? "Failed to fetch kitchen collections.";
            })

            /* Finishing shedule kitchen finishes */
            .addCase(fetchFSKitchenFinishList.pending, state => {
                state.loadingFSKitchenFinishes = true;
                state.errorFSKitchenFinishes = null;
            })
            .addCase(fetchFSKitchenFinishList.fulfilled, (state, action: PayloadAction<FSKitchenFinish[]>) => {
                state.loadingFSKitchenFinishes = false;
                state.fsKitchenFinishes = action.payload;
            })
            .addCase(fetchFSKitchenFinishList.rejected, (state, action) => {
                state.loadingFSKitchenFinishes = false;
                state.errorFSKitchenFinishes = action.error.message ?? "Failed to fetch kitchen finishes.";
            })

            /* Finishing shedule kitchen ranges */
            .addCase(fetchFSKitchenRangeList.pending, state => {
                state.loadingFSKitchenRanges = true;
                state.errorFSKitchenRanges = null;
            })
            .addCase(fetchFSKitchenRangeList.fulfilled, (state, action: PayloadAction<FSKitchenRange[]>) => {
                state.loadingFSKitchenRanges = false;
                state.fsKitchenRanges = action.payload;
            })
            .addCase(fetchFSKitchenRangeList.rejected, (state, action) => {
                state.loadingFSKitchenRanges = false;
                state.errorFSKitchenRanges = action.error.message ?? "Failed to fetch kitchen ranges.";
            })

            /* Finishing shedule kitchen styles */
            .addCase(fetchFSKitchenStyleList.pending, state => {
                state.loadingFSKitchenStyles = true;
                state.errorFSKitchenStyles = null;
            })
            .addCase(fetchFSKitchenStyleList.fulfilled, (state, action: PayloadAction<FSKitchenStyle[]>) => {
                state.loadingFSKitchenStyles = false;
                state.fsKitchenStyles = action.payload;
            })
            .addCase(fetchFSKitchenStyleList.rejected, (state, action) => {
                state.loadingFSKitchenStyles = false;
                state.errorFSKitchenStyles = action.error.message ?? "Failed to fetch kitchen styles.";
            })

            /* Finishing shedule light */
            .addCase(fetchFSLightList.pending, state => {
                state.loadingFSLights = true;
                state.errorFSLights = null;
            })
            .addCase(fetchFSLightList.fulfilled, (state, action: PayloadAction<FSLight[]>) => {
                state.loadingFSLights = false;
                state.fsLights = action.payload;
            })
            .addCase(fetchFSLightList.rejected, (state, action) => {
                state.loadingFSLights = false;
                state.errorFSLights = action.error.message ?? "Failed to fetch light.";
            })

            /* Finishing shedule other */
            .addCase(fetchFSOtherList.pending, state => {
                state.loadingFSOthers = true;
                state.errorFSOthers = null;
            })
            .addCase(fetchFSOtherList.fulfilled, (state, action: PayloadAction<FSOther[]>) => {
                state.loadingFSOthers = false;
                state.fsOthers = action.payload;
            })
            .addCase(fetchFSOtherList.rejected, (state, action) => {
                state.loadingFSOthers = false;
                state.errorFSOthers = action.error.message ?? "Failed to fetch others.";
            })

            /* Finishing shedule report */
            .addCase(fetchFSReportData.pending, state => {
                state.loadingFSReportData = true;
                state.errorFSReportData = null;
            })
            .addCase(fetchFSReportData.fulfilled, (state, action: PayloadAction<FSReportData | null>) => {
                state.loadingFSReportData = false;
                state.fsReportData = action.payload;
            })
            .addCase(fetchFSReportData.rejected, (state, action) => {
                state.loadingFSReportData = false;
                state.errorFSReportData = action.error.message ?? "Failed to fetch report data.";
            })
    }
})

export default finishingScheduleSlice.reducer